import { ColumnsType } from 'antd/es/table';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import classNames from 'classnames';
import { AutochargeStatus, Contract, redirectToContractFileLink, useGetPaginatedContractsQuery } from 'entities/Contract';
import { getUserName, useRole, useSendDebtorLastNotificationMutation } from 'entities/User';
import { ExportEntity, getContractColumnKeys, useOpenExportModal } from 'features/ExportModal';
import { NotifyLeaveDate } from 'features/NotifyLeaveDate';
import { useTableFilterContext } from 'features/TableFilter';
import { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as EditIcon } from 'shared/assets/icons/EditIcon.svg';
import { ReactComponent as EditPencilIcon } from 'shared/assets/icons/EditPencilIcon.svg';
import { ReactComponent as ExportIcon } from 'shared/assets/icons/ExportIcon.svg';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { ReactComponent as EmailIcon } from 'shared/assets/icons/EmailIcon.svg';
import { ReactComponent as PlusIcon } from 'shared/assets/icons/PlusIcon.svg';
import { ReactComponent as CancelIcon } from 'shared/assets/icons/CancelIcon.svg';
import { ServerTable } from 'shared/ui/ServerTable';
import { RowSideBadge } from 'shared/ui/ServerTable/RowSideBadge';
import { ServerTableActions, ServerTableRowActions } from 'shared/ui/ServerTable/types';
import { WarnTag } from 'shared/ui/WarnTag';
import { useQueryString } from 'shared/utils/helpers/convertQueryString';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { contractDetailsDrawerActions } from 'widgets/ContractDetailsDrawer';
import { createContractModalActions } from 'widgets/CreateContractModal';
import { contractPageActions } from '../model/slice/contractsPageSlice';
import { transformContractsListFilters } from '../utils/transformContractsListFilters';
import { ContractNoteDrawer } from './ContractNoteDrawer';
import { ContractsListFilter } from './ContractsListFilter';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { useGetCurrencySymbol } from 'app/appState';
import { checkInvoiceOverdueDate } from 'entities/Invoice';
import { useGlobalConfirmModal } from 'app/globalConfirmModal';
import { showNotification } from 'app/providers/NotificationsProvider';
import { Tag } from 'antd';
import { getColorTagByAccountType } from 'shared/utils/helpers/getColorTagByAccountType';
import { getSizeCodeTypeString } from 'entities/SizeCode';
import { SendContractClosureNotificationModal, useSendContractClosureNotification } from 'features/SendContractClosureNotificationModal';

export const ContractsTable: FC = memo(() => {
  const navigate = useNavigate();

  const { parsedQuery } = useQueryString();

  const currencySymbol = useGetCurrencySymbol();

  const { t } = useAppTranslation(['contracts', 'common', 'boxes', 'user']);
  const dispatch = useAppDispatch();

  const { isPartner } = useRole();

  const { openExportModal, isExportEnabled } = useOpenExportModal();

  const { setFiltersOpened, appliedFilters, tags, clearAllFilters } = useTableFilterContext();

  const [sendDebtorLastNotification] = useSendDebtorLastNotificationMutation();

  const { openModal, closeModal, setModalLoading } = useGlobalConfirmModal();

  const { selectedContract, openSendContractClosureNotificationModal, closeSendContractClosureNotificationModal } =
    useSendContractClosureNotification();

  useEffect(() => {
    if (parsedQuery.fileUrl) {
      redirectToContractFileLink(parsedQuery.fileUrl);
    }
  }, [parsedQuery.fileUrl]);

  const openContractDetails = useCallback(
    (contract: Contract): void => {
      dispatch(contractDetailsDrawerActions.setOpenContractDetails(contract.contractId));
    },
    [dispatch],
  );

  const openContractNotes = useCallback(
    (contract: Contract): void => {
      dispatch(contractPageActions.setOpenContractNoteDrawer(contract));
    },
    [dispatch],
  );

  const goToUserContracts = useCallback(
    (userId: string): void => {
      if (userId) {
        navigate(`${AppRoutes.USERS}/${userId}/contracts`);
      }
    },
    [navigate],
  );

  const handleDebtorLastNotification = useCallback(
    async (contractId: string): Promise<void> => {
      try {
        setModalLoading(true);

        await sendDebtorLastNotification(contractId).unwrap();

        showNotification('info', t('Success', { ns: 'common' }), t('Debtor last notification has been sent successfully', { ns: 'user' }));
      } catch (error) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when sending debtor last notification', { ns: 'user' }));
        console.log('error', error);
      } finally {
        setModalLoading(false);
        closeModal();
      }
    },
    [setModalLoading, sendDebtorLastNotification, t, closeModal],
  );

  const openDebtorNotificationConfirmModal = useCallback(
    (contract: Contract): void => {
      const overdueInvoices = contract.invoices?.filter((invoice) => invoice.debt > 0 && checkInvoiceOverdueDate(invoice.dueDate));

      openModal({
        title: t('Send debtor last notification', { ns: 'user' }),
        description: t('Are you sure you want to send debtor last notification mail?', { ns: 'user' }),
        onCancel: closeModal,
        onOk: async () => {
          await handleDebtorLastNotification(contract.contractId);
        },
        isConfirmDisabled: !overdueInvoices?.length,
      });
    },
    [closeModal, handleDebtorLastNotification, openModal, t],
  );

  const rowActions: Array<ServerTableRowActions<Contract>> = useMemo(
    () => [
      {
        name: 'editNote',
        icon: <EditIcon />,
        theme: 'clear',
        description: t('View/edit notes'),
        onClick: openContractNotes,
      },
      {
        name: 'contractInfo',
        label: t('Details'),
        description: t('Details'),
        onClick: openContractDetails,
      },
      {
        name: 'sendСlarification',
        theme: 'clear',
        label: t('Send debtor last notification', { ns: 'user' }),
        description: t('Send debtor last notification', { ns: 'user' }),
        icon: <CancelIcon />,
        onClick: openDebtorNotificationConfirmModal,
      },
      {
        name: 'sendContractClosureNotification',
        theme: 'clear',
        label: t('Notice of Intended Extraordinary Termination', { ns: 'user' }),
        description: t('Notice of Intended Extraordinary Termination', { ns: 'user' }),
        icon: <EmailIcon className="[&>path]:stroke-primary" />,
        isDisabled: (contract) => Boolean(contract.leaveDate),
        onClick: openSendContractClosureNotificationModal,
      },
    ],
    [openContractDetails, openContractNotes, openDebtorNotificationConfirmModal, openSendContractClosureNotificationModal, t],
  );

  const openCreateContractModal = useCallback((): void => {
    dispatch(createContractModalActions.setOpened(true));
  }, [dispatch]);

  const tableActions: Array<ServerTableActions<Contract>> = useMemo(() => {
    const exportAction: Array<ServerTableActions<Contract>> = isExportEnabled
      ? [
          {
            name: t('Export', { ns: 'common' }),
            theme: 'secondary',
            icon: <ExportIcon />,
            iconPosition: 'prev',
            onClick: () => {
              openExportModal({
                filters: transformContractsListFilters(appliedFilters),
                columns: getContractColumnKeys(t),
                entity: ExportEntity.CONTRACT,
              });
            },
          },
        ]
      : [];

    return [
      {
        name: t('Create Contract'),
        icon: <PlusIcon />,
        iconPosition: 'prev',
        onClick: openCreateContractModal,
        disabled: isPartner,
      },
      ...exportAction,
      {
        name: t('Filters', { ns: 'common' }),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          setFiltersOpened(true);
        },
      },
    ];
  }, [appliedFilters, isExportEnabled, isPartner, openCreateContractModal, openExportModal, setFiltersOpened, t]);

  const columns = useMemo<ColumnsType<Contract>>(
    () => [
      {
        title: t('Warehouse'),
        key: 'warehouse',
        width: '10%',
        render: (_, record) => (
          <div>
            <Link className="text-accent underline font-normal" to={`${AppRoutes.WAREHOUSES}/${record.warehouse.warehouseId}`}>
              {record.warehouse.name}
            </Link>
            {[AutochargeStatus.ENABLED, AutochargeStatus.STOPPED].includes(record.autocharges) && (
              <RowSideBadge
                color={record.autocharges === AutochargeStatus.ENABLED ? 'success' : 'warn'}
                toolTipColor="white"
                toolTipFontColor="primary"
                title={t('Autocharges {{status}}', { status: t(record.autocharges) })}
              />
            )}
          </div>
        ),
      },
      {
        title: t('Contract number'),
        key: 'contractNumber',
        width: '5%',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => (
          <div
            className="text-accent underline font-normal cursor-pointer"
            onClick={() => {
              openContractDetails(record);
            }}
          >
            #{record.contractNumber}
          </div>
        ),
      },
      {
        title: t('Customer'),
        key: 'customerEmail',
        render: (_, record) => (
          <div
            onClick={() => {
              goToUserContracts(record.user.userId);
            }}
            className="flex flex-col"
          >
            <div className="text-sm text-accent cursor-pointer underline"> {record.user.email}</div>
            <div className="font-bold text-accent cursor-pointer underline">{getUserName(record.user)}</div>
            <Tag className="w-fit" color={getColorTagByAccountType(record.user.accountType)}>
              {t(record.user.accountType, { ns: 'user' })}
            </Tag>
          </div>
        ),
      },
      {
        title: t('Area'),
        key: 'area',
        width: '10%',
        render: (_, record) => {
          const boxItem = record.contractItems?.find((item) => Boolean(item.box))?.box || null;

          return (
            <>
              <div>
                {boxItem?.square} {getSizeCodeTypeString(boxItem?.sizeCode?.sizeCodeType)}
              </div>
              <div className="font-normal text-sm">{boxItem?.name}</div>
            </>
          );
        },
      },
      {
        title: (
          <>
            <div>{t('Date of creation')}</div>
            <div>{t('Arrival Date')}</div>
          </>
        ),
        key: 'createdAt',
        width: '10%',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => (
          <>
            <div className="font-medium">{getClientDateFormat(record.createdAt)}</div>
            <div className="text-sm font-normal">{getClientDateFormat(record.startDate)}</div>
          </>
        ),
      },
      {
        title: t('Planned Departure Date'),
        key: 'noticeLeaveDate',
        width: '7%',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => (
          <div className="flex space-x-7">
            <div className="font-normal">{getClientDateFormat(record.noticeLeaveDate)}</div>

            {!record.leaveDate && (
              <NotifyLeaveDate
                contractId={record.contractId}
                invoiceFrequencyType={record.invoiceFrequencyType}
                initialEndDate={record.initialEndDate}
                noticeLeaveDate={record.noticeLeaveDate}
                action={<EditPencilIcon className="cursor-pointer" />}
              />
            )}
          </div>
        ),
      },
      {
        title: t('Closing Date'),
        key: 'leaveDate',
        width: '6%',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => getClientDateFormat(record.leaveDate),
      },
      // { title: t('Paid Days'), key: 'paidDays' }, // TODO получить последнюю оплату по договору, взять у неё конечную дату (date to). От неё отнять сегодняшнюю дату
      {
        title: (
          <>
            <div>{t('Overdue Periods')}</div>
            <div>{t('Debt')}</div>
          </>
        ),
        key: 'overduePeriods',
        width: '10%',
        render: (_, record) => {
          const overdueInvoices = record.invoices?.filter((invoice) => invoice.debt > 0 && checkInvoiceOverdueDate(invoice.dueDate));

          const totalDebt = roundNumber(overdueInvoices?.reduce((acc, invoice) => acc + invoice.debt, 0));

          const overduePeriodInvoices = overdueInvoices?.filter((invoice) => !invoice.relatedInvoiceId);

          if (!overduePeriodInvoices?.length) {
            return null;
          }

          return (
            <>
              <div className="tex-sm text-error font-semibold text-center">{overduePeriodInvoices.length}</div>
              <div className="tex-sm text-error font-semibold text-center">
                {totalDebt} {currencySymbol}
              </div>
            </>
          );
        },
      },
    ],
    [currencySymbol, goToUserContracts, openContractDetails, t],
  );

  const highlightRow = (record: Contract): string => {
    return classNames({
      'bg-secondary': !record.isApplicable,
    });
  };

  return (
    <div className="space-y-4">
      <WarnTag className="w-max" textSize="medium">
        {t('Attention! The contract can be closed only after the client takes their things from the box.')}
      </WarnTag>
      <ServerTable
        fetchData={useGetPaginatedContractsQuery}
        columns={columns}
        rowKey="contractId"
        title={t('Contracts')}
        tableActions={tableActions}
        tableActionsPosition="prev"
        rowActions={rowActions}
        search
        defaultFilters={transformContractsListFilters(appliedFilters)}
        searchPlaceholder={t('Enter customer or contract number')}
        tags={tags}
        onAllFiltersClear={clearAllFilters}
        rowClassName={highlightRow}
        rowActionsLimit={2}
      />
      <ContractsListFilter />
      <ContractNoteDrawer />
      <SendContractClosureNotificationModal contract={selectedContract} onClose={closeSendContractClosureNotificationModal} />
    </div>
  );
});
